import type { QueryClient } from '@tanstack/react-query';
import { clearOrgPlanningCommandSequencersForFolder } from '../domains/orgchart/hooks/orgPlanningCommandSequencer';
import { useOrgPlanningScenarioStore } from '../domains/orgchart/stores/orgPlanningScenarioStore';
import { useSuccessionOptimisticSlateStore } from '../domains/orgchart/stores/successionOptimisticSlateStore';
import { invalidateProtectedPlanningFolder } from '../domains/orgchart/hooks/protectedPlanningGeneration';
import { usePositionStructureOptimisticStore } from '../domains/orgchart/stores/positionStructureOptimisticStore';

/**
 * A protected-data clearance change invalidates every cached projection for
 * the folder. Removing inactive values prevents a later mount from reusing a
 * projection compiled under an older grant.
 */
export async function purgeFolderScopedQueries(
  queryClient: QueryClient,
  folderId: string | undefined,
): Promise<void> {
  if (!folderId) return;
  const predicate = (query: { queryKey: readonly unknown[] }) =>
    query.queryKey.some((part) => part === folderId);

  // Query data is only one representation of a protected plan. Abort queued
  // commands and purge the tab-local protected slate before any active query
  // is allowed to refetch under the new policy. Scenario selection is cleared
  // only for diagrams observed in this folder's local state or query cache.
  invalidateProtectedPlanningFolder(folderId);
  usePositionStructureOptimisticStore.getState().clear(folderId);
  clearOrgPlanningCommandSequencersForFolder(folderId);
  const diagramIds = new Set<string>();
  const optimistic = useSuccessionOptimisticSlateStore.getState();
  for (const context of Object.keys(optimistic.slatesByContext)) {
    if (!context.startsWith(`${folderId}:`)) continue;
    diagramIds.add(context.slice(folderId.length + 1).split('|')[0]);
    optimistic.clear(context);
  }
  for (const query of queryClient.getQueryCache().findAll({ predicate })) {
    const key = query.queryKey;
    if (key[0] === 'atlas-succession' && typeof key[2] === 'string') diagramIds.add(key[2]);
    if (key[0] === 'atlas' && key[1] === 'organization-plans' && typeof key[3] === 'string') {
      diagramIds.add(key[3]);
    }
  }
  for (const diagramId of diagramIds) {
    useOrgPlanningScenarioStore.getState().select(diagramId, null);
  }

  // Folder shell queries (name/children/access/archived) carry no protected
  // record values — but they DO gate the dashboard's skeleton: resetting them
  // to pending swaps the whole folder view for a loading skeleton, destroying
  // every mounted dialog (the "import window closes by itself" reports).
  // Background-invalidate the shells; hard-reset everything else.
  const isFolderShellQuery = (key: readonly unknown[]) =>
    key[0] === 'folder'
    && key[1] === folderId
    && (key.length === 2 || SHELL_QUERY_SEGMENTS.has(key[2] as string));
  const protectedPredicate = (query: { queryKey: readonly unknown[] }) =>
    predicate(query) && !isFolderShellQuery(query.queryKey);
  await queryClient.cancelQueries({ predicate: protectedPredicate });
  queryClient.removeQueries({ predicate: protectedPredicate, type: 'inactive' });
  await queryClient.resetQueries({ predicate: protectedPredicate, type: 'active' });
  await queryClient.invalidateQueries({
    predicate: (query) => predicate(query) && isFolderShellQuery(query.queryKey),
    refetchType: 'active',
  });
}

const SHELL_QUERY_SEGMENTS = new Set(['data', 'children', 'access', 'archived']);

/**
 * Soft counterpart for strictly ADDITIVE policy changes (a grant/ACL/binding
 * was created): active queries refetch in the background while keeping their
 * current data mounted. The hard purge above resets active queries to their
 * loading state, which unmounts whole folder views mid-interaction — on a
 * freshly created Atlas folder, the owner's own provisioning event echoed
 * back through the invalidation feed and closed the open "Import people"
 * dialog (QA report). Nothing rendered under the old policy over-discloses
 * when clearance only widened, so the loading flip is pure UX damage there.
 */
export async function refreshFolderScopedQueries(
  queryClient: QueryClient,
  folderId: string | undefined,
): Promise<void> {
  if (!folderId) return;
  const predicate = (query: { queryKey: readonly unknown[] }) =>
    query.queryKey.some((part) => part === folderId);
  queryClient.removeQueries({ predicate, type: 'inactive' });
  await queryClient.invalidateQueries({ predicate, refetchType: 'active' });
}
